/**
 * <ix-scroller> — minimal horizontal carousel built on native scroll-snap.
 * Shared by the sections ported from the Interactive theme.
 *
 * Markup contract:
 *   <ix-scroller data-autoplay="0" data-loop>
 *     <div class="ix-scroller__track">…slides…</div>
 *     <button data-dir="prev" hidden>…</button>   (optional)
 *     <button data-dir="next" hidden>…</button>   (optional)
 *     <div class="ix-scroller__dots"></div>       (optional)
 *   </ix-scroller>
 *
 * Scrolling itself is pure CSS — this component only wires up the optional
 * arrow buttons, dots and autoplay, so it stays tiny and the carousel works
 * without JavaScript.
 */
class IxScroller extends HTMLElement {
  #timer = null;

  connectedCallback() {
    this.track = this.querySelector('.ix-scroller__track');
    if (!this.track) return;

    this.buttons = Array.from(this.querySelectorAll('button[data-dir]'));
    this.dots = this.querySelector('.ix-scroller__dots');

    this.buttons.forEach((button) => {
      button.hidden = false;
      button.addEventListener('click', () => this.#scrollByPage(button.dataset.dir === 'next' ? 1 : -1));
    });

    if (this.dots) this.#buildDots();

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    new ResizeObserver(() => this.#update()).observe(this.track);
    this.#update();

    const autoplay = Number(this.dataset.autoplay || 0);
    if (autoplay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#setupAutoplay(autoplay);
    }
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  get #slides() {
    return Array.from(this.track.children);
  }

  #scrollByPage(direction) {
    const width = this.track.clientWidth;
    const max = this.track.scrollWidth - width;
    let left = this.track.scrollLeft + direction * width * 0.8;

    if (this.hasAttribute('data-loop')) {
      if (direction > 0 && this.track.scrollLeft >= max - 4) left = 0;
      if (direction < 0 && this.track.scrollLeft <= 4) left = max;
    }

    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  #onScroll = () => {
    if (this.#pending) return;
    this.#pending = true;
    requestAnimationFrame(() => {
      this.#pending = false;
      this.#update();
    });
  };

  #pending = false;

  #update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const overflow = max > 4;

    this.toggleAttribute('data-overflow', overflow);

    if (!this.hasAttribute('data-loop')) {
      this.buttons.forEach((button) => {
        const next = button.dataset.dir === 'next';
        button.disabled = next ? this.track.scrollLeft >= max - 4 : this.track.scrollLeft <= 4;
      });
    }

    if (this.dots?.childElementCount) {
      const index = this.#nearestIndex();
      Array.from(this.dots.children).forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  #nearestIndex() {
    const slides = this.#slides;
    let nearest = 0;
    let nearestDistance = Infinity;
    slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = i;
      }
    });
    return nearest;
  }

  #buildDots() {
    this.#slides.forEach((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ix-scroller__dot';
      dot.setAttribute('aria-label', `${i + 1}`);
      dot.addEventListener('click', () => {
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      });
      this.dots.appendChild(dot);
    });
  }

  #setupAutoplay(seconds) {
    const start = () => {
      clearInterval(this.#timer);
      this.#timer = setInterval(() => this.#scrollByPage(1), seconds * 1000);
    };
    const stop = () => clearInterval(this.#timer);

    if (this.dataset.pauseHover === 'true') {
      this.addEventListener('pointerenter', stop);
      this.addEventListener('pointerleave', start);
    }

    new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
    }).observe(this);
  }
}

if (!customElements.get('ix-scroller')) {
  customElements.define('ix-scroller', IxScroller);
}
